import { useMemo } from 'react';
import classNames from 'classnames';
import chatGPTLogo from 'src/images/logos/chat-gpt.svg';
import claudeLogo from 'src/images/logos/anthropic-claude-logo.svg';
import googlePalmLogo from 'src/images/logos/google-palm-logo.svg';
import googleGeminiProLogo from 'src/images/logos/google-gemini-pro-logo.svg';
import ninjaLogoIcon from 'src/images/ninja-blue-logo-icon.svg';
import avatarNinja from 'src/images/avatars/AiAvatarNinja.png';
import attention from 'src/images/icons/AttentionDot.svg';
import company from 'src/images/icons/Windows.png';
import defaultAvatar from 'src/images/icons/defaultAvatar.svg';
import waveform from 'src/images/icons/waveform.svg';
import ninjaIcon from 'src/images/logos/ninja-head-new.png';
import openaiIcon from 'src/images/logos/openai-icon.svg';
import gmailLogo from 'src/images/logos/gmail-logo.svg';
import slackLogo from 'src/images/logos/slack-logo.svg';
import calendar from 'src/images/icons/calendar.png';
import contact from 'src/images/icons/contact.png';
import googleWhite from 'src/images/logos/google-white-logo.svg';
import landscape from 'src/images/icons/landscape.svg';
import portrait from 'src/images/icons/portrait.svg';
import square from 'src/images/icons/square.svg';
import metaLogo from 'src/images/logos/meta-logo.svg';
import envelope from 'src/images/icons/envelope.svg';
import listGradient from 'src/images/icons/list-gradient.svg';
import googleCalendarLogo from 'src/images/logos/google-calendar-logo.svg';
import outlookLogo from 'src/images/logos/outlook-logo.svg';
import awsLogo from 'src/images/logos/aws-logo.png';
import cohereLogo from 'src/images/logos/cohere-logo.svg';
import jurassicLogo from 'src/images/logos/jurassic-logo.png';
import mistralLogo from 'src/images/logos/mistral-logo.png';
import bedrockStableLogo from 'src/images/logos/bedrock-stable-logo.svg';
import { getCircleColor } from 'src/utils/index';
import { ChatGPTLogo } from 'src/images/logos/chat-gpt';
import { AWSLogo } from 'src/images/logos/aws-logo';
import { AnthropicLogo } from 'src/images/logos/anthropic-logo';
import ninjaPixProLogo from 'src/images/logos/ninja-pix-pro-logo.svg';
import ninjaPixLogo from 'src/images/logos/ninja-pix-logo.svg';
import { NinjaAgentsOutlinedIcon } from 'src/images/icons/ninjaAgentsOutlined';
import { HeadCircuit } from '@phosphor-icons/react';
import ninjaIconLight from 'src/images/icons/ninja-icon-light.svg';
import nova from 'src/images/logos/nova.svg';

import { DeepSeekLogo } from 'src/images/logos/deepSeekLogo';
import { MetaLogo } from 'src/images/logos/meta-logo';
import { AlibabaLogo } from 'src/images/logos/alibaba-logo';
import { XaiLogo } from 'src/images/logos/xai-logo';

const iconMap = {
  company,
  attention,
  ninjaIcon,
  openaiIcon,
  avatarNinja,
  defaultAvatar,
  waveform,
  gmailLogo,
  slackLogo,
  calendar,
  contact,
  googleWhite,
  landscape,
  portrait,
  square,
  chatGPTLogo,
  claudeLogo,
  metaLogo,
  googlePalmLogo,
  googleGeminiProLogo,
  ninjaLogoIcon,
  envelope,
  googleCalendarLogo,
  outlookLogo,
  listGradient,
  awsLogo,
  cohereLogo,
  jurassicLogo,
  mistralLogo,
  bedrockStableLogo,
  ninjaPixProLogo, // images for ninja models
  ninjaPixLogo,
  ninjaIconLight,
  nova,
  superAgent: 'superAgent',
  ninjaOutlined: 'ninjaOutlined',
  deepSeekLogo: 'deepSeekLogo',
  metaLogoJSX: 'metaLogoJSX',
  alibabaLogoJSX: 'alibabaLogoJSX',
  xaiLogoJSX: 'xaiLogoJSX',
};

export type IconName = keyof typeof iconMap;

type IconProps = {
  src?: string;
  type?: IconName | null;
  alt?: string;
  size?: number;
  masterColor?: string;
  rest?: { [key: string]: React.ReactNode };
};

/**
 * Icon component displays icon with an image based
 * on the props provided, or a color circle instead.
 */
export const Icon = ({
  src = '',
  type,
  size = 32,
  alt = '',
  masterColor = '',
  ...rest
}: IconProps) => {
  const custom = useMemo(() => {
    switch (type) {
      case 'awsLogo':
        return <AWSLogo />;
      case 'chatGPTLogo':
        return <ChatGPTLogo />;
      case 'claudeLogo':
        return <AnthropicLogo />;
      case 'ninjaOutlined':
        return <NinjaAgentsOutlinedIcon width={size} height={size} />;
      case 'superAgent':
        return <HeadCircuit size={size} />;
      case 'deepSeekLogo':
        return <DeepSeekLogo width={size} height={size} />;
      case 'metaLogoJSX':
        return <MetaLogo size={size} />;
      case 'alibabaLogoJSX':
        return <AlibabaLogo size={size} />;
      case 'xaiLogoJSX':
        return <XaiLogo size={size} />;

      default:
        return null;
    }
  }, [type, size]);

  const color = useMemo(() => masterColor || getCircleColor(), [masterColor]);
  const avatar = src !== '' ? src : type ? iconMap[type] : '';
  const styles = {
    width: `${size}px`,
    height: `${size}px`,
    lineHeight: `${size}px`,
  };

  if (custom) {
    return (
      <span className="icon" style={styles}>
        {custom}
      </span>
    );
  }

  const circleClasses = classNames('circle', color);
  return (
    <span className="icon" style={styles}>
      {avatar ? (
        <img
          referrerPolicy="no-referrer"
          src={avatar}
          alt={alt}
          width={size}
          height={size}
          {...rest}
        />
      ) : (
        <span data-testid="circle-icon" className={circleClasses}>
          {alt}
        </span>
      )}
    </span>
  );
};
